import { useState, useEffect, useRef, useCallback } from "react";
import { devError } from "@/lib/logger";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { gameService } from "@/services";
import { io } from "socket.io-client";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Plus,
  Minus,
  Zap,
  Swords,
  Wifi,
  Monitor,
  Trophy,
  User,
  Star,
  LogOut,
  Crown,
  Copy,
  Check,
  RefreshCw,
  Bot,
  X,
  Eye,
  Calendar,
  Settings,
  MailWarning,
  BookOpen,
} from "lucide-react";
import { authService } from "@/services";
import MobileMenu from "@/components/MobileMenu";
import PlayerNameLink from "@/components/PlayerNameLink";
import { useActiveMode } from "@/hooks/useActiveMode";
import MarketFeed from "@/components/game/social/MarketFeed";
import HowToPlayStrip from "@/components/game/hud/HowToPlayStrip";
import DailyCountdown from "@/components/game/hud/DailyCountdown";

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;

export default function LobbyPage() {
  const { t } = useTranslation();
  usePageTitle("Lobby");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // When a limited-time mode is live, today's daily challenge tile
  // re-themes to match (color, headline, subtitle). Off-window: tile
  // renders with its default purple gradient and stock copy.
  const activeMode = useActiveMode();

  // Local game state
  const [playerCount, setPlayerCount] = useState(2);
  const [names, setNames] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);

  // vs AI state
  const [aiBotCount, setAiBotCount] = useState(1);
  const [aiDifficulty, setAiDifficulty] = useState("medium");

  // Online game state
  const [mode, setMode] = useState("local");
  const [socket, setSocket] = useState(null);
  const [roomCode, setRoomCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [roomPlayers, setRoomPlayers] = useState([]);
  const [roomGameId, setRoomGameId] = useState(null);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [publicRooms, setPublicRooms] = useState([]);
  const [activeGames, setActiveGames] = useState([]);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isHost, setIsHost] = useState(false);

  // Matchmaking state
  const [matchmaking, setMatchmaking] = useState(false);
  const [matchStatus, setMatchStatus] = useState("");
  const [matchPreferredSize, setMatchPreferredSize] = useState(2);

  // Auto-join room if URL has ?code=XYZ (invite link support).
  // Also stash the code so the socket-ready effect below can fire
  // join_room as soon as the connection lands — the "set mode +
  // prefill input" behaviour wasn't enough; users had to manually
  // click Join after the redirect.
  const autoJoinCodeRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code && code.length >= 4) {
      const upper = code.toUpperCase();
      setMode("online");
      setJoinCode(upper);
      autoJoinCodeRef.current = upper;
    }
  }, []);

  // Connect socket for online mode
  const socketRef = useRef(null);

  useEffect(() => {
    if (mode !== "online") {
      // Disconnect when leaving online mode
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
      return;
    }

    // Already connected
    if (socketRef.current) return;

    const s = io(SOCKET_URL, {
      path: "/api/socket.io",
      transports: ["websocket", "polling"],
    });
    socketRef.current = s;

    s.on("connect", () => {
      console.log("Socket connected:", s.id);
      setSocket(s);
      // Fire the deferred auto-join from `?code=XYZ` once we have a
      // live socket. V-3 binds identity from the session cookie at
      // connect, so the room handler sees the right user_id.
      const pending = autoJoinCodeRef.current;
      if (pending) {
        autoJoinCodeRef.current = null;
        s.emit("join_room", {
          room_code: pending,
          name: user?.name || "Player",
        });
      }
    });

    // Mirror the active room into the URL so reload doesn't wipe the
    // waiting-room view. Auto-join effect (above) reads `?code=`
    // on mount and re-emits join_room; the backend's existing-
    // member branch reroutes the player straight back to the room
    // (or into the game if it's already started).
    const stampCodeInUrl = (code) => {
      if (!code) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get("code") === code) return;
      url.searchParams.set("code", code);
      window.history.replaceState(null, "", url.toString());
    };
    s.on("room_created", (data) => {
      setRoomCode(data.room_code);
      setRoomGameId(data.game_id);
      setRoomPlayers(data.players);
      setIsHost(true);
      stampCodeInUrl(data.room_code);
    });
    s.on("room_updated", (data) => {
      setRoomPlayers(data.players);
      setRoomGameId(data.game_id);
      setRoomCode(data.room_code);
      // Reload + auto-rejoin emits room_updated (not room_created),
      // so isHost wouldn't otherwise restore for the original host.
      // Player[0] is always the room creator per the backend's
      // append-only roster model.
      const firstUserId = data?.players?.[0]?.user_id;
      if (firstUserId && user?.user_id) {
        setIsHost(firstUserId === user.user_id);
      }
      stampCodeInUrl(data.room_code);
    });
    // Room teardown (host cancelled, or this client left). Clear the
    // waiting-room view and strip ?code= so reload doesn't re-join.
    const clearRoomState = () => {
      setRoomCode("");
      setRoomGameId(null);
      setRoomPlayers([]);
      setIsHost(false);
      const url = new URL(window.location.href);
      if (url.searchParams.has("code")) {
        url.searchParams.delete("code");
        window.history.replaceState(null, "", url.toString());
      }
    };
    s.on("room_left", clearRoomState);
    s.on("room_closed", clearRoomState);
    s.on("game_started", (data) => {
      navigate(`/game/${data.game_id}?online=true`);
    });
    s.on("room_error", (data) => {
      setMatchmaking(false);
      toast.error(data.message || t("lobby.online.room.errorJoin"));
    });
    s.on("public_rooms", (data) => setPublicRooms(data));
    s.on("matchmaking_status", (data) => {
      if (data.status === "searching") {
        setMatchmaking(true);
        setMatchStatus(data.message || t("lobby.matchmaking.default"));
      } else if (data.status === "cancelled") {
        setMatchmaking(false);
        setMatchStatus("");
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, navigate]);

  const updateName = (idx, val) => {
    const n = [...names];
    n[idx] = val;
    setNames(n);
  };

  const createLocalGame = async () => {
    setLoading(true);
    try {
      const playerNames = names
        .slice(0, playerCount)
        .map((n, i) => n.trim() || `Player ${i + 1}`);
      const data = await gameService.createLocalGame(
        playerNames,
        user?.user_id,
      );
      navigate(`/game/${data.game_id}`);
    } catch {
      // Axios interceptor already showed an error toast; just reset state.
    }
    setLoading(false);
  };

  const createVsAIGame = async () => {
    setLoading(true);
    try {
      const data = await gameService.createVsAI({
        userName: user?.name || "Player",
        botCount: aiBotCount,
        difficulty: aiDifficulty,
      });
      navigate(`/game/${data.game_id}?vsai=true`);
    } catch {
      // Axios interceptor already showed an error toast; just reset state.
    }
    setLoading(false);
  };

  const createRoom = () => {
    if (!socket) return;
    socket.emit("create_room", {
      user_id: user?.user_id,
      name: user?.name || "Player",
      max_players: maxPlayers,
    });
  };

  const joinRoom = () => {
    if (!socket || !joinCode.trim()) return;
    socket.emit("join_room", {
      room_code: joinCode.trim().toUpperCase(),
      user_id: user?.user_id,
      name: user?.name || "Player",
    });
  };

  const cancelRoom = () => {
    if (socket && roomGameId) {
      socket.emit("leave_room", { game_id: roomGameId });
    }
    // Optimistic local reset; room_left/room_closed confirms from server.
    setRoomCode("");
    setRoomGameId(null);
    setRoomPlayers([]);
    setIsHost(false);
    const url = new URL(window.location.href);
    if (url.searchParams.has("code")) {
      url.searchParams.delete("code");
      window.history.replaceState(null, "", url.toString());
    }
  };

  const startOnlineGame = () => {
    if (!socket || !roomGameId) return;
    socket.emit("start_online_game", { game_id: roomGameId });
  };

  const refreshRooms = () => {
    if (socket) socket.emit("get_public_rooms", {});
  };

  const loadActiveGames = async () => {
    try {
      // Limit to past hour so the spectate list doesn't surface
      // stale/abandoned rooms (per product feedback §"spectate
      // during gameplay should only show games live during the past
      // 1 hour").
      const data = await gameService.listGames({ limit: 50, sinceMinutes: 60 });
      const items = data?.items || [];
      setActiveGames(items.filter((g) => g.status === "active").slice(0, 10));
    } catch (err) {
      devError("Failed to load active games:", err);
    }
  };

  // Auto-load active games when in online mode
  useEffect(() => {
    if (mode === "online") loadActiveGames();
  }, [mode]);

  const findMatch = () => {
    if (!socket) return;
    socket.emit("find_match", {
      user_id: user?.user_id,
      name: user?.name || "Player",
      preferred_size: matchPreferredSize,
    });
  };

  const cancelMatch = () => {
    if (!socket) return;
    socket.emit("cancel_match", {});
  };

  const inviteLink = roomCode
    ? `${window.location.origin}/?code=${roomCode}`
    : "";
  const copyInviteLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(roomCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = async () => {
    if (socket) socket.disconnect();
    await logout();
    navigate("/login");
  };

  return (
    <div
      className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 sm:p-6"
      id="main-content"
      role="main"
      tabIndex={-1}
      data-testid="lobby-page"
    >
      <div className="max-w-6xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-8 h-8 rounded-full border-2 border-[#38B6FF]"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#38B6FF] flex items-center justify-center text-white text-sm font-bold">
                {user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <span className="text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD]">
              {user?.name}
            </span>
          </div>
          <nav className="flex" aria-label={t("lobby.label.accountNav")}>
            <MobileMenu>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/profile")}
                data-testid="profile-btn"
                className="justify-start rounded-full sm:justify-center"
                aria-label={t("lobby.label.profile")}
              >
                <User className="w-4 h-4 mr-2 sm:mr-1" aria-hidden="true" />{" "}
                <span>{t("lobby.nav.profile")}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/season-pass")}
                data-testid="season-pass-btn"
                className="justify-start rounded-full sm:justify-center"
                aria-label={t("lobby.label.seasonPass")}
              >
                <Star className="w-4 h-4 mr-2 sm:mr-1" aria-hidden="true" />{" "}
                <span>{t("lobby.nav.season")}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/leaderboard")}
                data-testid="leaderboard-btn"
                className="justify-start rounded-full sm:justify-center"
                aria-label={t("lobby.label.leaderboard")}
              >
                <Trophy className="w-4 h-4 mr-2 sm:mr-1" aria-hidden="true" />{" "}
                <span>{t("lobby.nav.leaderboard")}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/rules")}
                data-testid="rules-btn"
                className="justify-start rounded-full sm:justify-center"
                aria-label={t("lobby.nav.rules")}
              >
                <BookOpen className="w-4 h-4 mr-2 sm:mr-1" aria-hidden="true" />{" "}
                <span>{t("lobby.nav.rules")}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/settings")}
                data-testid="settings-btn"
                className="justify-start rounded-full sm:justify-center"
                aria-label={t("lobby.label.settings")}
              >
                <Settings className="w-4 h-4 mr-2 sm:mr-1" aria-hidden="true" />{" "}
                <span>{t("lobby.nav.settings")}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="rounded-full text-[#FF5A5F] justify-start sm:justify-center"
                aria-label={t("lobby.label.logOut")}
              >
                <LogOut className="w-4 h-4 mr-2 sm:mr-0" aria-hidden="true" />{" "}
                <span className="sm:hidden">{t("lobby.label.logOut")}</span>
              </Button>
            </MobileMenu>
          </nav>
        </div>

        {/* Title */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-3 mb-3">
            <Swords className="w-10 h-10 text-[#38B6FF]" />
            <h1
              className="text-4xl sm:text-5xl font-bold text-[#1A237E] dark:text-[#E3F2FD] tracking-tight"
              style={{ fontFamily: "Fredoka" }}
            >
              {t("lobby.title")}
            </h1>
          </div>
          <p className="text-base text-[#1A237E]/60 dark:text-[#E3F2FD]/60">
            {t("lobby.subtitle")}
          </p>
        </div>

        {/* Email-not-verified nudge. Only renders for email/password
            accounts whose `email_verified` is still false (Google
            users are auto-verified upstream). The "Resend" button
            calls /auth/resend-verification — backend rate limits
            5/min so click-spamming is safe. */}
        {user &&
          user.auth_type !== "google" &&
          user.email_verified === false && (
            <UnverifiedEmailBanner email={user.email} t={t} />
          )}

        {/* How to play — 3-bullet orientation strip for first-time users */}
        <HowToPlayStrip />

        {/* Daily Challenge featured banner — secondary action,
            visually distinct from main Play CTA. When a limited-time
            mode is live, the tile re-themes (color, headline,
            subtitle) so the "today's special game" surface stays
            singular. Falls back to the default purple gradient
            off-window. */}
        <button
          onClick={() => navigate("/daily-challenge")}
          data-testid="daily-challenge-btn"
          className="group w-full mb-6 rounded-3xl p-5 sm:p-6 text-left text-white shadow-[0_6px_0_rgba(0,0,0,0.15)] hover:shadow-[0_3px_0_rgba(0,0,0,0.15)] hover:translate-y-[3px] transition-all active:translate-y-[6px] active:shadow-none flex items-center gap-4"
          style={
            activeMode
              ? {
                  background: `linear-gradient(135deg, ${activeMode.theme_color}EE, ${activeMode.theme_color}AA)`,
                }
              : {
                  background:
                    "linear-gradient(135deg, rgb(168 85 247), rgb(79 70 229))",
                }
          }
        >
          <div className="flex items-center justify-center w-12 h-12 shrink-0 sm:w-14 sm:h-14 rounded-2xl bg-white/25 dark:bg-slate-900/25">
            <Calendar className="w-6 h-6 sm:w-7 sm:h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider bg-white/20 dark:bg-slate-900/20 rounded-full px-2 py-0.5">
                {t("lobby.daily.today")}
              </span>
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white text-purple-700 rounded-full px-2 py-0.5"
                data-testid="daily-limited-badge"
                style={activeMode ? { color: activeMode.theme_color } : undefined}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-urgency-blink" />{" "}
                {activeMode ? t("liveMode.label") : t("lobby.daily.limited")}
              </span>
              <DailyCountdown variant="warm" testId="lobby-daily-countdown" />
            </div>
            <h2
              className="text-lg font-bold leading-tight sm:text-xl"
              style={{ fontFamily: "Fredoka" }}
            >
              {activeMode ? activeMode.name : t("lobby.daily.title")}
            </h2>
            <p className="text-xs sm:text-sm opacity-90">
              {activeMode ? activeMode.description : t("lobby.daily.subtitle")}
            </p>
          </div>
          {/* Explicit CTA so the banner reads as clickable. It's a styled
              span, not a button — the whole banner is already a <button>,
              and nesting buttons is invalid HTML. */}
          <span
            data-testid="daily-play-now"
            className="inline-flex items-center gap-1.5 shrink-0 rounded-full bg-white/25 dark:bg-slate-900/25 px-3 py-1.5 text-sm font-bold group-hover:bg-white/35 transition-colors"
          >
            <Zap className="w-4 h-4" /> {t("lobby.daily.playNow")}
          </span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div>
            {/* Mode Tabs */}
            <Tabs value={mode} onValueChange={setMode} className="mb-6">
              <TabsList className="grid h-12 grid-cols-3 gap-1 p-1 bg-white/50 dark:bg-slate-900/50 rounded-2xl">
                <TabsTrigger
                  value="local"
                  className="rounded-xl font-semibold data-[state=active]:bg-[#1A237E] data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
                  data-testid="local-mode-tab"
                >
                  <Monitor className="w-4 h-4 mr-2" /> {t("lobby.local.label")}
                </TabsTrigger>
                <TabsTrigger
                  value="ai"
                  className="rounded-xl font-semibold data-[state=active]:bg-[#8247E5] data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
                  data-testid="ai-mode-tab"
                >
                  <Bot className="w-4 h-4 mr-2" /> {t("lobby.ai.label")}
                </TabsTrigger>
                <TabsTrigger
                  value="online"
                  className="rounded-xl font-semibold data-[state=active]:bg-[#38B6FF] data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
                  data-testid="online-mode-tab"
                >
                  <Wifi className="w-4 h-4 mr-2" /> {t("lobby.online.label")}
                </TabsTrigger>
              </TabsList>

              {/* LOCAL MODE */}
              <TabsContent value="local">
                <div className="p-8 border-2 shadow-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-5 h-5 text-[#38B6FF]" />
                    <h2
                      className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD]"
                      style={{ fontFamily: "Fredoka" }}
                    >
                      {t("lobby.local.title")}
                    </h2>
                    <div className="flex items-center gap-2 ml-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        data-testid="decrease-players-btn"
                        onClick={() =>
                          setPlayerCount(Math.max(2, playerCount - 1))
                        }
                        disabled={playerCount <= 2}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] w-6 text-center">
                        {playerCount}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        data-testid="increase-players-btn"
                        onClick={() =>
                          setPlayerCount(Math.min(4, playerCount + 1))
                        }
                        disabled={playerCount >= 4}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p
                    className="text-xs text-[#1A237E]/55 dark:text-[#E3F2FD]/55 mb-5"
                    data-testid="local-mode-sub"
                  >
                    {t("lobby.local.subtitle")}
                  </p>
                  <div className="mb-6 space-y-3">
                    {Array.from({ length: playerCount }, (_, i) => (
                      <Input
                        key={`player-input-${i}`}
                        data-testid={`player-name-input-${i}`}
                        placeholder={t("lobby.local.playerPlaceholder", {
                          n: i + 1,
                        })}
                        value={names[i]}
                        onChange={(e) => updateName(i, e.target.value)}
                        className="bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-12"
                      />
                    ))}
                  </div>
                  <Button
                    data-testid="create-game-btn"
                    disabled={loading}
                    onClick={createLocalGame}
                    className="w-full h-14 rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed animate-soft-pulse-ring"
                  >
                    <Zap className="w-5 h-5 mr-2" />{" "}
                    {loading
                      ? t("lobby.local.button.starting")
                      : t("lobby.local.button.start")}
                  </Button>
                </div>
              </TabsContent>

              {/* VS AI MODE */}
              <TabsContent value="ai">
                <div
                  className="p-6 border-2 shadow-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl"
                  data-testid="vs-ai-panel"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Bot className="w-5 h-5 text-[#8247E5]" />
                    <h2
                      className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD]"
                      style={{ fontFamily: "Fredoka" }}
                    >
                      {t("lobby.ai.title")}
                    </h2>
                  </div>
                  <p
                    className="text-xs text-[#1A237E]/55 dark:text-[#E3F2FD]/55 mb-4"
                    data-testid="ai-mode-sub"
                  >
                    {t("lobby.ai.subtitle")}
                  </p>

                  <div className="mb-4">
                    <label className="text-xs font-bold text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-2 block">
                      {t("lobby.ai.opponents")}
                    </label>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        data-testid="ai-decrease-bots-btn"
                        onClick={() =>
                          setAiBotCount(Math.max(1, aiBotCount - 1))
                        }
                        disabled={aiBotCount <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span
                        className="text-2xl font-bold text-[#1A237E] dark:text-[#E3F2FD] w-8 text-center"
                        data-testid="ai-bot-count"
                        style={{ fontFamily: "Fredoka" }}
                      >
                        {aiBotCount}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        data-testid="ai-increase-bots-btn"
                        onClick={() =>
                          setAiBotCount(Math.min(3, aiBotCount + 1))
                        }
                        disabled={aiBotCount >= 3}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 ml-2">
                        {t("lobby.ai.botCountSuffix", { count: aiBotCount })}
                      </span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="text-xs font-bold text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-2 block uppercase tracking-wider">
                      {t("lobby.ai.difficulty.label")}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {["easy", "medium", "hard"].map((d) => (
                        <button
                          key={d}
                          type="button"
                          data-testid={`ai-difficulty-${d}`}
                          onClick={() => setAiDifficulty(d)}
                          className={`rounded-2xl h-12 capitalize font-bold text-sm transition-all border-2 ${
                            aiDifficulty === d
                              ? "bg-[#8247E5] text-white border-[#8247E5] shadow-[0_3px_0_rgba(0,0,0,0.2)] scale-[1.02]"
                              : "bg-white/60 dark:bg-slate-900/60 text-[#1A237E]/70 dark:text-[#E3F2FD]/70 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 hover:border-[#8247E5]/40 hover:text-[#1A237E] dark:text-[#E3F2FD]"
                          }`}
                        >
                          {t(`lobby.ai.difficulty.${d}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    data-testid="create-vs-ai-btn"
                    disabled={loading}
                    onClick={createVsAIGame}
                    className="w-full h-14 rounded-full bg-[#8247E5] hover:bg-[#6B35C7] text-white font-bold text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed animate-soft-pulse-ring animate-soft-pulse-ring-purple"
                  >
                    <Bot className="w-5 h-5 mr-2" />{" "}
                    {loading
                      ? t("lobby.ai.button.starting")
                      : t("lobby.ai.button.start")}
                  </Button>
                </div>
              </TabsContent>

              {/* ONLINE MODE */}
              <TabsContent value="online">
                {roomCode ? (
                  /* In a room - waiting for players */
                  <div className="p-6 border-2 shadow-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl">
                    <div className="mb-4 text-center">
                      <p className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-1">
                        {t("lobby.online.room.roomCode")}
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className="text-3xl font-bold text-[#38B6FF] tracking-[0.3em]"
                          style={{ fontFamily: "Fredoka" }}
                          data-testid="room-code"
                        >
                          {roomCode}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={copyCode}
                          data-testid="copy-code-btn"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-[#1A237E]/40 dark:text-[#E3F2FD]/40 mt-1">
                        {t("lobby.online.room.share")}
                      </p>

                      {/* Invite link */}
                      <div className="mt-3 flex items-center justify-center gap-2 bg-[#38B6FF]/5 rounded-xl px-3 py-2 border border-[#38B6FF]/10">
                        <span
                          className="text-[11px] text-[#1A237E]/70 dark:text-[#E3F2FD]/70 truncate flex-1"
                          data-testid="invite-link"
                        >
                          {inviteLink}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={copyInviteLink}
                          data-testid="copy-invite-link-btn"
                          className="px-2 shrink-0 h-7"
                        >
                          {linkCopied ? (
                            <>
                              <Check className="w-3 h-3 mr-1 text-green-500" />{" "}
                              <span className="text-[10px]">
                                {t("lobby.online.room.copied")}
                              </span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 mr-1" />{" "}
                              <span className="text-[10px]">
                                {t("lobby.online.room.copyLink")}
                              </span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="mb-4 space-y-2">
                      {roomPlayers.map((p, i) => (
                        <div
                          key={`rp-${p.user_id || p.name}-${i}`}
                          className="flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-slate-900/60 rounded-xl"
                          data-testid={`room-player-${i}`}
                        >
                          {i === 0 && (
                            <Crown className="w-4 h-4 text-[#FFC107]" />
                          )}
                          <PlayerNameLink
                            userId={p.user_id}
                            name={p.name}
                            className="text-sm font-semibold text-[#1A237E] dark:text-[#E3F2FD]"
                            testId={`room-player-name-${i}`}
                          />
                          {p.ready && (
                            <Badge className="ml-auto text-[10px] bg-green-100 text-green-700">
                              {t("lobby.online.ready")}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                    {isHost && (
                      <Button
                        data-testid="start-online-game-btn"
                        onClick={startOnlineGame}
                        disabled={roomPlayers.length < 2}
                        className="w-full h-14 rounded-full bg-[#00E676] hover:bg-[#00C853] text-white font-bold text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Zap className="w-5 h-5 mr-2" />{" "}
                        {t("lobby.online.room.startMatch", {
                          count: roomPlayers.length,
                        })}
                      </Button>
                    )}
                    {!isHost && (
                      <p className="text-center text-sm text-[#1A237E]/50 dark:text-[#E3F2FD]/50">
                        {t("lobby.online.room.waitingForHost")}
                      </p>
                    )}
                    <Button
                      variant="outline"
                      onClick={cancelRoom}
                      data-testid="cancel-room-btn"
                      className="w-full mt-3 rounded-full border-2 border-[#FF5A5F]/40 text-[#FF5A5F] hover:bg-[#FF5A5F]/10"
                    >
                      <X className="w-4 h-4 mr-2" />{" "}
                      {isHost
                        ? t("lobby.online.room.cancel")
                        : t("lobby.online.room.leave")}
                    </Button>
                  </div>
                ) : matchmaking ? (
                  /* Matchmaking in progress */
                  <div
                    className="p-6 text-center border-2 shadow-xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl"
                    data-testid="matchmaking-panel"
                  >
                    <div className="relative inline-flex items-center justify-center mb-4">
                      <div className="absolute inset-0 rounded-full bg-[#38B6FF]/20 animate-ping" />
                      <div className="relative w-16 h-16 rounded-full bg-[#38B6FF] flex items-center justify-center">
                        <Users className="w-8 h-8 text-white animate-pulse" />
                      </div>
                    </div>
                    <h3
                      className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1"
                      style={{ fontFamily: "Fredoka" }}
                    >
                      {t("lobby.matchmaking.title")}
                    </h3>
                    <p
                      className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-5"
                      data-testid="matchmaking-status"
                    >
                      {matchStatus || t("lobby.matchmaking.default")}
                    </p>
                    <Button
                      data-testid="cancel-match-btn"
                      variant="outline"
                      onClick={cancelMatch}
                      className="rounded-full border-2 border-[#FF5A5F]/40 text-[#FF5A5F] hover:bg-[#FF5A5F]/10 font-bold px-6"
                    >
                      <X className="w-4 h-4 mr-2" />{" "}
                      {t("lobby.matchmaking.cancel")}
                    </Button>
                  </div>
                ) : (
                  /* Create or Join or Find Match */
                  <div className="space-y-4">
                    {/* Quick Match — dominant CTA, random matchmaking */}
                    <div className="bg-gradient-to-br from-[#38B6FF]/15 via-white/70 to-[#8247E5]/10 backdrop-blur-xl border-2 border-[#38B6FF]/30 shadow-[0_6px_0_rgba(56,182,255,0.15)] rounded-3xl p-7">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-5 h-5 text-[#38B6FF]" />
                        <h3
                          className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]"
                          style={{ fontFamily: "Fredoka" }}
                        >
                          {t("lobby.online.quickMatchTitle")}
                        </h3>
                        <Badge className="ml-auto text-[10px] bg-[#38B6FF] text-white">
                          {t("lobby.online.recommended")}
                        </Badge>
                      </div>
                      <p
                        className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4"
                        data-testid="online-mode-sub"
                      >
                        {t("lobby.online.quickMatchSub")}
                      </p>
                      <div className="flex items-center gap-3 mb-5">
                        <span className="text-xs font-semibold text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
                          {t("lobby.online.players")}
                        </span>
                        {[2, 3, 4].map((n) => (
                          <Button
                            key={n}
                            size="sm"
                            variant={
                              matchPreferredSize === n ? "default" : "outline"
                            }
                            className={`rounded-full w-9 h-9 p-0 font-bold transition-all ${
                              matchPreferredSize === n
                                ? "bg-[#38B6FF] text-white border-2 border-[#38B6FF] shadow-[0_2px_0_rgba(0,0,0,0.15)] scale-105"
                                : "border-2 border-[#1A237E]/15 dark:border-[#E3F2FD]/15 text-[#1A237E]/70 dark:text-[#E3F2FD]/70 hover:border-[#38B6FF]/50"
                            }`}
                            onClick={() => setMatchPreferredSize(n)}
                            data-testid={`match-size-${n}`}
                          >
                            {n}
                          </Button>
                        ))}
                      </div>
                      <Button
                        data-testid="find-match-btn"
                        onClick={findMatch}
                        className="w-full h-14 rounded-full bg-gradient-to-r from-[#38B6FF] to-[#8247E5] hover:opacity-95 text-white font-bold text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed animate-soft-pulse-ring"
                      >
                        <Zap className="w-5 h-5 mr-2" />{" "}
                        {t("lobby.online.findMatch")}
                      </Button>
                    </div>

                    {/* Private Room — secondary: create or join a specific code */}
                    <div className="p-6 border-2 shadow-lg bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-[#1A237E]/60 dark:text-[#E3F2FD]/60" />
                        <h3
                          className="text-sm font-bold text-[#1A237E]/80 dark:text-[#E3F2FD]/80 uppercase tracking-wider"
                          style={{ fontFamily: "Fredoka" }}
                        >
                          {t("lobby.online.playWithFriends")}
                        </h3>
                      </div>
                      <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50 mb-4">
                        {t("lobby.online.playWithFriendsSub")}
                      </p>

                      <div className="grid grid-cols-1 gap-3 mb-4 sm:grid-cols-2">
                        {/* Create */}
                        <div className="bg-white/60 dark:bg-slate-900/60 rounded-2xl p-4 border border-[#1A237E]/5 dark:border-[#E3F2FD]/5">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-semibold text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
                              {t("lobby.online.max")}
                            </span>
                            {[2, 3, 4].map((n) => (
                              <Button
                                key={n}
                                size="sm"
                                variant={
                                  maxPlayers === n ? "default" : "outline"
                                }
                                className={`rounded-full w-7 h-7 p-0 text-xs font-bold ${
                                  maxPlayers === n
                                    ? "bg-[#1A237E] text-white border-2 border-[#1A237E]"
                                    : "border-2 border-[#1A237E]/15 dark:border-[#E3F2FD]/15 text-[#1A237E]/60 dark:text-[#E3F2FD]/60"
                                }`}
                                onClick={() => setMaxPlayers(n)}
                                data-testid={`max-players-${n}`}
                              >
                                {n}
                              </Button>
                            ))}
                          </div>
                          <Button
                            data-testid="create-room-btn"
                            onClick={createRoom}
                            className="w-full h-10 rounded-full bg-white hover:bg-[#E3F2FD] dark:bg-[#0a1428] text-[#1A237E] dark:text-[#E3F2FD] border-2 border-[#1A237E]/15 dark:border-[#E3F2FD]/15 hover:border-[#38B6FF]/50 font-bold text-sm transition-all"
                          >
                            <Wifi className="w-4 h-4 mr-2" />{" "}
                            {t("lobby.online.createRoom")}
                          </Button>
                        </div>

                        {/* Join */}
                        <div className="bg-white/60 dark:bg-slate-900/60 rounded-2xl p-4 border border-[#1A237E]/5 dark:border-[#E3F2FD]/5">
                          <Input
                            data-testid="join-code-input"
                            placeholder={t("lobby.online.joinPlaceholder")}
                            value={joinCode}
                            onChange={(e) =>
                              setJoinCode(e.target.value.toUpperCase())
                            }
                            // Pin both bg and text colors for dark
                            // mode: previously bg-white was hardcoded
                            // while text inherited the dark page's
                            // light text → invisible characters while
                            // typing.
                            className="bg-white dark:bg-slate-900 text-[#1A237E] dark:text-[#E3F2FD] placeholder:text-[#1A237E]/40 dark:placeholder:text-[#E3F2FD]/40 border-2 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-9 mb-2 uppercase tracking-widest text-center font-bold text-sm focus:border-[#38B6FF]"
                            maxLength={8}
                          />
                          <Button
                            data-testid="join-room-btn"
                            onClick={joinRoom}
                            disabled={joinCode.length < 6}
                            className="w-full h-10 rounded-full bg-[#00E676] hover:bg-[#00C853] text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {t("lobby.online.joinRoom")}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Public Rooms */}
                    <div className="p-6 border-2 shadow-lg bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/40 rounded-3xl">
                      <div className="flex items-center justify-between mb-3">
                        <h3
                          className="text-sm font-bold text-[#1A237E]/80 dark:text-[#E3F2FD]/80 uppercase tracking-wider"
                          style={{ fontFamily: "Fredoka" }}
                        >
                          {t("lobby.online.browseLive")}
                        </h3>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={loadActiveGames}
                            data-testid="refresh-active-games-btn"
                            title={t("lobby.online.refreshActive")}
                            className="w-8 h-8 p-0 rounded-full"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={refreshRooms}
                            data-testid="refresh-rooms-btn"
                            className="w-8 h-8 p-0 rounded-full"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {publicRooms.length === 0 && activeGames.length === 0 ? (
                        <p className="text-sm text-[#1A237E]/40 dark:text-[#E3F2FD]/40 text-center py-6">
                          {t("lobby.online.emptyRooms")}
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {publicRooms.map((room) => (
                            <div
                              key={room.game_id}
                              className="flex items-center justify-between bg-white/60 dark:bg-slate-900/60 rounded-xl px-4 py-3 border border-[#1A237E]/5 dark:border-[#E3F2FD]/5"
                              data-testid={`public-room-${room.room_code}`}
                            >
                              <div>
                                <span className="text-sm font-bold text-[#38B6FF] tracking-widest">
                                  {room.room_code}
                                </span>
                                <span className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50 ml-2">
                                  {t("lobby.online.room.waitingStatus", {
                                    playerCount: room.player_count,
                                    maxPlayers: room.max_players,
                                  })}
                                </span>
                              </div>
                              <Button
                                size="sm"
                                className="rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white h-8 px-4 font-bold text-xs"
                                onClick={() => {
                                  setJoinCode(room.room_code);
                                  joinRoom();
                                }}
                              >
                                {t("lobby.online.join")}
                              </Button>
                            </div>
                          ))}
                          {activeGames.map((g) => (
                            <div
                              key={`active-${g.game_id}`}
                              className="flex items-center gap-3 bg-[#8247E5]/5 rounded-xl px-4 py-3 border border-[#8247E5]/20"
                              data-testid={`active-game-${g.game_id}`}
                            >
                              {/* Names column. min-w-0 + flex-1 lets the
                                  inner truncate-block compute its own
                                  width; without it, long names push the
                                  Spectate button off-row on mobile. */}
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                <span className="shrink-0 text-[10px] font-bold text-[#8247E5] bg-[#8247E5]/10 rounded-full px-2 py-0.5">
                                  {t("lobby.online.live")}
                                </span>
                                <span className="block min-w-0 flex-1 text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60 truncate">
                                  {(g.player_names || [])
                                    .slice(0, 3)
                                    .join(", ")}
                                </span>
                              </div>
                              {/* Mobile: icon-only square so player
                                  names get the full row width. sm+
                                  (≥640px) the label appears alongside
                                  the icon. aria-label keeps screen
                                  readers covered when the text is
                                  hidden. */}
                              <Button
                                size="sm"
                                variant="outline"
                                className="shrink-0 rounded-full border-2 border-[#8247E5]/40 text-[#8247E5] hover:bg-[#8247E5]/10 h-8 w-8 p-0 sm:w-auto sm:px-3 font-bold text-xs"
                                onClick={() =>
                                  navigate(`/spectate/${g.game_id}`)
                                }
                                data-testid={`spectate-${g.game_id}-btn`}
                                aria-label={t("lobby.online.room.spectate")}
                                title={t("lobby.online.room.spectate")}
                              >
                                <Eye className="w-3.5 h-3.5 sm:mr-1" />
                                <span className="hidden sm:inline">
                                  {t("lobby.online.room.spectate")}
                                </span>
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT SIDEBAR — Live Market Feed */}
          <MarketFeed />
        </div>
      </div>
    </div>
  );
}

function UnverifiedEmailBanner({ email, t }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const handleResend = async () => {
    if (sending) return;
    setSending(true);
    try {
      await authService.resendVerification(email);
      setSent(true);
      toast.success(t("lobby.unverifiedEmail.toastTitle"), {
        description: t("lobby.unverifiedEmail.toastDescription", { email }),
      });
    } catch {
      // axios interceptor already toasts errors
    }
    setSending(false);
  };
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 mb-6 border rounded-2xl border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20"
      data-testid="unverified-email-banner"
      role="status"
    >
      <MailWarning
        className="w-5 h-5 text-amber-600 shrink-0"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
          {t("lobby.unverifiedEmail.title")}
        </p>
        <p className="text-xs truncate text-amber-800/80 dark:text-amber-200/80">
          {t("lobby.unverifiedEmail.body", { email })}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleResend}
        disabled={sending || sent}
        className="rounded-full border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-800/30 whitespace-nowrap"
        data-testid="resend-verification-btn"
      >
        {sent
          ? t("lobby.unverifiedEmail.sent")
          : sending
            ? t("lobby.unverifiedEmail.resending")
            : t("lobby.unverifiedEmail.resend")}
      </Button>
    </div>
  );
}
